import { useState } from "react";
import { Box, Button, Pagination, Tooltip, Typography, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { useNavigate, useParams } from "react-router-dom";

import { entityGenderEnum } from "../../enums/entity.enum";
import { UserInfo } from "../../types";
import CompareModal from "../../shared/CompareModal";
import useGoStarListing from "../../api/queries/gostar/goStarQueries";
import { colors } from "../../theme/palette";
import APP_ROUTES from "../../routers/routeConstants";

import GoStarTabs from "./GoStarTabs";
import GoStarRoles from "./GoStarRoles";
import GoStarFilter from "./GoStarFilter";
import UserItem, { UserLoadingItem } from "./UserItem";

const GoStar = () => {
  const { t } = useTranslation();

  const { query } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(0);
  const [tooltipMessage, setTooltipMessage] = useState<string>(t("components.compare.compareWarning"));
  const [tabValue, setTabValue] = useState<number>(0);
  const [roleTab, setRoleTab] = useState<number>(
    query === "talents" ? 0 : query === "professionals" ? 1 : query === "entities" ? 2 : 0
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gender, setGender] = useState<{
    label: string;
    value: entityGenderEnum;
  } | null>(null);
  const [nationality, setNationality] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [country, setCountry] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [persona, setPersona] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [compareData, setCompareData] = useState<any>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [toolTipShowMsg, setToolTipShowMsg] = useState(false);

  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangeChildTabValue = (newValue: number) => {
    setRoleTab(newValue);
    setPage(0);
    setPersona(null);

    if (!query) return;
    navigate(APP_ROUTES.GO_STAR, { replace: true });
  };

  const { goStarData, isGoStarDataLoading, isGoStarDataHasError } = useGoStarListing({
    tabValue,
    page,
    nationality,
    country,
    gender,
    persona,
    roleTab,
  });

  const goStarList = goStarData?.items || [];
  const paginationData = goStarData?.meta || {
    currentPage: 0,
    itemCount: 0,
    itemsPerPage: 0,
    totalItems: 0,
    totalPages: 0,
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const canCompare = (): boolean => {
    let isComparable: boolean = true;
    setTooltipMessage(t("components.compare.compareWarning"));

    if (compareData.length < 2) {
      setTooltipMessage(t("components.compare.compareWarning"));
      isComparable = false;
      return false;
    }

    let role: string = compareData[0].role;

    compareData.forEach((el: any) => {
      if (el.role !== role) {
        isComparable = false;
        setTooltipMessage(t("components.compare.compareWarningPersonas"));
        return;
      }
    });

    return isComparable;
  };

  const checkCompareSelection = (value: any) => {
    let selectedData = [...compareData];
    if (!selectedData.find((el: any) => el.id === value?.id)) {
      selectedData.push({ id: value?.id, role: value?.role });
    } else {
      selectedData = selectedData.filter((el: any) => el.id !== value?.id);
    }
    setCompareData(selectedData);
  };

  return (
    <Box
      sx={{
        padding: "12px",
        background: colors.White,
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        width: "870px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "22px",
          width: "100%",
          marginBottom: "22px",
        }}
      >
        <Typography variant="h4ExtraBold">{t("pages.goStar.goStar")}</Typography>
        <GoStarTabs
          tabValue={tabValue}
          handleChangeTabValue={handleChangeTabValue}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
        <GoStarRoles
          roleTab={roleTab}
          handleChangeChildTabValue={handleChangeChildTabValue}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
        <GoStarFilter
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          gender={gender}
          setGender={setGender}
          nationality={nationality}
          setNationality={setNationality}
          country={country}
          setCountry={setCountry}
          persona={persona}
          setPersona={setPersona}
          roleTab={roleTab}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "22px",
        }}
      >
        <Typography
          variant="body13Bold"
          sx={{
            fontWeight: 500,
            color: colors.Black100,
            display: "flex",
            alignItems: "center",
            columnGap: "8px",
          }}
        >
          {isGoStarDataLoading ? (
            <Skeleton
              variant="rounded"
              animation="wave"
              sx={{
                width: "50px",
              }}
            />
          ) : (
            paginationData.totalItems
          )}

          {roleTab === 0
            ? ` ${t("pages.goStar.talents")}`
            : roleTab === 1
            ? persona
              ? ` ${persona.label}`
              : ` ${t("pages.goStar.professionals")}`
            : ` ${t("pages.goStar.entities")}`}
        </Typography>

        {roleTab !== 2 ? (
          <>
            <Button
              variant="text-sm"
              onClick={() => {
                const isComparable = canCompare();
                if (isComparable) {
                  setShowCompareModal(true);
                } else {
                  setToolTipShowMsg(true);
                  setTimeout(() => {
                    setToolTipShowMsg(false);
                  }, 1000);
                }
              }}
              endIcon={
                <Tooltip open={toolTipShowMsg} title={tooltipMessage} arrow>
                  <InfoRoundedIcon />
                </Tooltip>
              }
              sx={{
                padding: "0",
                fontWeight: 400,
                textDecoration: "underline",
                "&:hover": {
                  background: "transparent",
                },
              }}
            >
              {t("pages.goStar.compare")}
            </Button>
            <CompareModal
              openModal={showCompareModal}
              compareData={compareData}
              setCompareData={setCompareData}
              closeModal={() => setShowCompareModal(false)}
            />
          </>
        ) : (
          <></>
        )}
      </Box>
      {isGoStarDataLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
          {[...Array(14)].map((_, idx) => (
            <UserLoadingItem key={idx} isLast={idx === 13} />
          ))}
        </Box>
      )}
      {goStarList.length === 0 && !isGoStarDataLoading && "No data..."}
      {goStarList.length > 0 && !isGoStarDataLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
          {goStarList.map((user: UserInfo) => {
            return (
              <UserItem
                key={user.id}
                roleTab={roleTab}
                user={user}
                compareData={compareData}
                checkCompareSelection={checkCompareSelection}
              />
            );
          })}
        </Box>
      )}
      {!isGoStarDataLoading && (
        <Pagination
          count={paginationData?.totalPages}
          page={!paginationData?.totalItems || paginationData?.totalItems <= 0 ? 1 : page + 1}
          onChange={handleChangePage}
          sx={{
            margin: "33px auto 0",
            direction: "ltr !important",
            "& .MuiPagination-ul": {
              justifyContent: "center",
            },
            "& .MuiButtonBase-root": {
              borderRadius: "8px !important",
            },
          }}
        />
      )}
    </Box>
  );
};

export default GoStar;
